import { List, Tooltip, Zoom } from '@mui/material';
import { useIsMobile } from '@side/pantry';
import { useAtomValue } from 'jotai';
import { type MouseEvent, useState } from 'react';
import {
  type MissingFieldsData,
  missingFieldsInformation,
} from 'src/state/navigation';
import { MissingRequiredFieldLink } from 'src/views/Coversheet/components/MissingRequiredFieldLink';
import { Prompt } from '@/components/Prompt/Prompt';
import { getMissingFieldLabel } from '@/views/Coversheet/utils/cardUtils';
import { TrackCoverSheetTaggingEvent } from '$analytics/useTrackAnalytics/domains/coverSheet/events';
import { useTrackCoverSheetAnalytics } from '$analytics/useTrackAnalytics/domains/coverSheet/useTrackCoverSheetAnalytics';
import { Badge, BadgeCount } from './ButtonBadge';

const DEFAULT_MAX = 99;

export type Props = {
  count?: number;
  tooltip?: string;
  max?: number;
  label?: string;
  collapsed?: boolean;
  formPath?: string;
  missingFieldsData?: MissingFieldsData[];
};

export const CoverSheetLinkBadge = ({
  count,
  tooltip,
  label,
  max = DEFAULT_MAX,
  collapsed = false,
  formPath,
  missingFieldsData: missingFieldsDataProp,
}: Props) => {
  const trackEvent = useTrackCoverSheetAnalytics();
  const isMobile = useIsMobile('md');
  const missingFieldsDataState = useAtomValue(missingFieldsInformation);
  const missingFieldsData = missingFieldsDataProp ?? missingFieldsDataState;
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<Element>) => {
    trackEvent(TrackCoverSheetTaggingEvent.ClickMissingFieldsView, {
      missingFields: missingFieldsData
        .map((field) =>
          getMissingFieldLabel(
            field.index,
            field.isAddable,
            field.label,
            field.cardTitle,
          ),
        )
        .join(', '),
    });
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: MouseEvent<Element>) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const displayValue = Number(count) > max ? `${max}+` : count;
  const badge = (
    <Zoom in={!!count}>
      <Badge
        aria-label={`${label} missing required fields`}
        id={`${label}-missing-required-fields`}
        role="button"
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
        }}
      >
        <BadgeCount data-testid="required-fields-count">
          {displayValue}
        </BadgeCount>
      </Badge>
    </Zoom>
  );

  if (missingFieldsData.length > 0 && !isMobile) {
    return (
      <>
        {badge}
        <Prompt
          anchorElement={anchorEl}
          labelledBy={`${label}-missing-required-fields`}
          icon={
            <Badge onClick={handleClose}>
              <BadgeCount>{displayValue}</BadgeCount>
            </Badge>
          }
          title="These fields are required to access documents"
          open={open}
          handleClose={(e) => handleClose(e)}
        >
          <List sx={{ pr: 5, py: 0 }}>
            {missingFieldsData.map((field) => {
              const linkLabel = getMissingFieldLabel(
                field.index,
                field.isAddable,
                field.label,
                field.cardTitle,
              );

              if ((field.section === label && !collapsed) || collapsed) {
                return (
                  <MissingRequiredFieldLink
                    key={field.name}
                    cardButtonId={`${field.cardId}-${field.index}-expand`}
                    name={field.name}
                    label={linkLabel}
                    formPath={formPath}
                    onClick={handleClose}
                  />
                );
              }

              return null;
            })}
          </List>
        </Prompt>
      </>
    );
  }

  if (tooltip)
    return (
      <Tooltip title={tooltip} placement="right">
        {badge}
      </Tooltip>
    );

  return badge;
};
